import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search, Package, RotateCcw, ChevronRight, CheckCircle2 } from "lucide-react";

const STATUS_META = {
  pending: { label: "Pending", color: "bg-[#D97706]/10 text-[#D97706]" },
  confirmed: { label: "Confirmed", color: "bg-[#D9822B]/10 text-[#D9822B]" },
  shipped: { label: "On its way", color: "bg-[#5F7355]/10 text-[#5F7355]" },
  delivered: { label: "Delivered", color: "bg-[#2D6A4F]/10 text-[#2D6A4F]" },
  cancelled: { label: "Cancelled", color: "bg-[#9E3B21]/10 text-[#9E3B21]" },
};

export default function MyOrders() {
  const [phone, setPhone] = useState(() => localStorage.getItem("da_customer_phone") || "");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const { items: cartItems, addItem, addGiftBox, addBulkBox, openCart } = useCart();
  const { gift_box_price, bulk_box_price } = useSettings();
  const navigate = useNavigate();

  const lookup = async (e) => {
    if (e) e.preventDefault();
    const clean = phone.replace(/\D/g, "");
    if (clean.length < 10) {
      toast.error("Enter a 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/orders/lookup", { phone: clean });
      setOrders(res.data);
      localStorage.setItem("da_customer_phone", clean);
      if (res.data.length === 0) toast.info("No orders yet — go grab some pickles!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const reorder = async (order) => {
    // Fetch fresh product data to make sure prices are current
    const productsRes = await api.get("/products");
    const products = productsRes.data;
    let added = 0;
    for (const item of order.items) {
      if (item.is_gift_box) {
        const slugs = item.gift_box_items || [];
        const names = slugs.map((s) => products.find((p) => p.slug === s)?.name || s);
        if (slugs.length === 3) {
          addGiftBox({ selectedSlugs: slugs, selectedNames: names, note: item.gift_note || "", price: gift_box_price || 549 });
          added++;
        }
      } else if (item.is_bulk_box) {
        const slugs = item.bulk_box_items || [];
        const names = slugs.map((s) => products.find((p) => p.slug === s)?.name || s);
        if (slugs.length === 6) {
          addBulkBox({ selectedSlugs: slugs, selectedNames: names, price: bulk_box_price || 999 });
          added++;
        }
      } else {
        const p = products.find((pp) => pp.id === item.product_id);
        if (p) {
          addItem(p, item.weight, item.quantity);
          added++;
        }
      }
    }
    if (added > 0) {
      toast.success(`Added ${added} item${added > 1 ? "s" : ""} from your last order`);
      openCart();
    } else {
      toast.error("Sorry — those pickles are no longer available");
    }
  };

  return (
    <div className="fade-up max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="text-center mb-10">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#9E3B21] mb-3">✦ Reorder Corner</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold">Your pickle history</h1>
        <p className="mt-3 text-[#6E675F]">Enter your phone number to find your past orders and re-order in one tap.</p>
      </div>

      <form onSubmit={lookup} className="bg-white border border-[#E6DEC3] rounded-2xl p-6 mb-8">
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <Label>Your phone number</Label>
            <Input
              data-testid="lookup-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="10-digit phone (e.g. 9876543210)"
              maxLength={13}
              className="mt-1.5"
            />
          </div>
          <Button
            data-testid="lookup-submit"
            type="submit"
            disabled={loading}
            className="btn-primary rounded-full px-6 py-6"
          >
            <Search className="w-4 h-4 mr-2" />
            {loading ? "Finding..." : "Find my orders"}
          </Button>
        </div>
      </form>

      {orders === null && (
        <p className="text-center text-sm text-[#6E675F]">Your orders will show up here.</p>
      )}

      {orders && orders.length === 0 && (
        <div className="text-center py-16 bg-white border border-[#E6DEC3] rounded-2xl">
          <Package className="w-14 h-14 text-[#D9822B] mx-auto mb-4" />
          <p className="font-serif text-xl">No past orders yet</p>
          <p className="text-sm text-[#6E675F] mt-2 mb-6">Start with a jar — you&apos;ll build a rich history soon.</p>
          <Button data-testid="lookup-shop-btn" onClick={() => navigate("/shop")} className="btn-primary rounded-full px-8">
            Browse Pickles
          </Button>
        </div>
      )}

      {orders && orders.length > 0 && (
        <div className="space-y-4" data-testid="lookup-results">
          {orders.map((o) => {
            const meta = STATUS_META[o.status] || STATUS_META.pending;
            return (
              <div
                key={o.order_number}
                data-testid={`lookup-order-${o.order_number}`}
                className="bg-white border border-[#E6DEC3] rounded-2xl p-5 flex flex-col sm:flex-row gap-4"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1.5">
                    <span className="font-mono text-sm font-semibold">{o.order_number}</span>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                  </div>
                  <div className="text-sm text-[#4A423C]">
                    {o.items.length} item{o.items.length > 1 ? "s" : ""} · {formatINR(o.total)}
                  </div>
                  <ul className="mt-1 text-xs text-[#6E675F]">
                    {o.items.slice(0, 3).map((i, idx) => (
                      <li key={`${i.product_id || i.product_name}-${i.weight}-${idx}`}>{i.product_name} · {i.weight} × {i.quantity}</li>
                    ))}
                    {o.items.length > 3 && <li>and {o.items.length - 3} more...</li>}
                  </ul>
                  <div className="text-xs text-[#6E675F] mt-2">{new Date(o.created_at).toLocaleString()}</div>
                </div>
                <div className="flex sm:flex-col gap-2 sm:items-end">
                  <Button
                    data-testid={`reorder-${o.order_number}`}
                    onClick={() => reorder(o)}
                    className="btn-primary rounded-full"
                    size="sm"
                  >
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Reorder
                  </Button>
                  <Link to={`/order-success/${o.order_number}`}>
                    <Button data-testid={`view-lookup-${o.order_number}`} variant="ghost" size="sm">
                      View <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
